(function() {
    'use strict';

    angular.module( APP_ID )
        .directive( 'dynamicBlock', [ '$compile', '$interpolate', 'BlockRecipesBuilder', 'LoggerFactory', 'ContentSwitchingService', DynamicBlockDirective ] );


    /**
     * Renders a block dynamically based on given `contentType` and rendering
     * `mode`.
     *
     * @attribute content - Main data object for the block (comes from API).
     * @attribute type - ID of the block. This ID needs to match
     * @attribute custom-data - Data object for additional data to be passed
     * down to the dynamic block(s).
     * @attribute mode - `nested` for dynamic blocks within other dynamic blocks
     * `dynamic` (default) for first level of dynamic blocks.
     *
     *
     * @param {ng.ICompileService} $compile
     * @param {ng.IInterpolateService} $interpolate
     * @param {BlockRecipesBuilder} BlockRecipesBuilder
     * @param {LoggerFactory} LoggerFactory
     * @param {ContentSwitchingService} ContentSwitchingService
     *
     * @type {ng.IDirectiveCompileFn}
     */
    function DynamicBlockDirective(
        $compile,
        $interpolate,
        BlockRecipesBuilder,
        LoggerFactory,
        ContentSwitchingService
    ){





        /* ===== PRIVATE MEMBERS ============================================ */


        /** @type {Logger} */
        var logger = LoggerFactory.createLogger( 'DynamicBlockDirective' );


        /** @const */
        var DEFAULT_RENDERING_MODE = 'default',


        CONTENT_TYPES = {
            INTERNATIONAL: 'international',
            DOMESTIC:      'domestic'
        };


        /** @type {ng.IDirective} */
        var directiveConfig = {
            restrict: 'A',
            priority: 1,
            scope: {
                content:    '<',
                type:       '<',
                customData: '<',
                mode:       '@'  // 'dynamic' || 'nested'
            },
            link: link
        };


        /** @type {Object<string,Object>} */
        var cachedDynamicBlocks = {};



        ////////////////////////////////////////////////////////////////////////

        return directiveConfig;

        ///////////////////////////////////////////////////////////////////////



        /**
         * @param {ng.IScope} $scope
         * @param {ng.IElement} element
         */
        function link( $scope, element ) {





            /* ===== PRIVATE MEMBERS ======================================== */


            /** @type {DOMElement} */
            var compiledBlockElement = null;





            /* ===== RUN ON INSTANTIATION =================================== */


            /** @constructor */
            (function init(){

                if ( shouldRender() ) {
                    generateAndCompileDirective();
                } else {
                    element.addClass( 'blank' );

                    logger.info( "Dynamic block `%s` specified only for a `%s` region and will not be rendered for the given content type...", $scope.type, $scope.content.region );
                }

            })();


            /** @destructor */
            (function destroy(){
                element.on( '$destroy', function () {
                    if ( compiledBlockElement ) {
                        compiledBlockElement.remove();
                        compiledBlockElement = null;
                    }
                });
            })();





            /* ===== PRIVATE FUNCTIONS ====================================== */


            /**
             * Retrieves the dynamic block's compilation function and runs it to
             * render the dynamic block.
             */
            function generateAndCompileDirective() {
                var renderingMode = $scope.mode || DEFAULT_RENDERING_MODE,
                blockType         = $scope.type,
                dynamicBlock      = prepareDynamicBlock( blockType, renderingMode );

                dynamicBlock.compile( $scope, function( clonedElement ){
                    compiledBlockElement = clonedElement;
                    element.append( compiledBlockElement );
                });

                decorateFromRecipeConfig( dynamicBlock.config );
            }


            /**
             * Evaluates whether or not a dynamic block is restricted to
             * a certain content mode (e.g. only international or domestic).
             *
             * If it is and the app runs in a different mode than specified,
             * it returns false. Otherwise, it returns true.
             *
             * @returns {boolean}
             */
            function shouldRender() {
                var contentType = $scope.content.region;

                return !( ( !ContentSwitchingService.isInternational() && contentType === CONTENT_TYPES.INTERNATIONAL ) ||
                    ( ContentSwitchingService.isInternational() && contentType === CONTENT_TYPES.DOMESTIC ) );
            }


            /**
             * Looks into the {Recipe}'s config and performs any custom
             * decorations based on the configuration options - For example
             * adding specific classes to the directive itself based on a
             * configuration attribute `parentClasses`.
             *
             * @param {RecipeConfig} configObject Dynamic block's rendering
             * configuration (see `BlockRecipesBuilder.DEFAULT_CONFIG`)
             */
            function decorateFromRecipeConfig( configObject ){
                if ( configObject.parentClasses && configObject.parentClasses.length > 0 ){
                    element.addClass( configObject.parentClasses.join(' ') );
                }
            }
        }





        /* ===== PRIVATE FUNCTIONS ========================================== */


        /**
         * Tries to retrieve the correct Recipe (Recipe configuration object +
         * dynamically assembled HTML template) based on the `blockType`
         * and `renderingMode`.
         *
         * @param {string} blockType Unique identifier of the dynamic block
         * type.
         * @param {string} renderingMode Mode in which a dynamic block should
         * be rendered (eg. 'default', 'nested'...).
         *
         * @returns {Recipe}
         */
        function getBlockDirectiveRecipe( blockType, renderingMode ){
            if ( BlockRecipesBuilder.recipes[ renderingMode ].hasOwnProperty( blockType ) ){
                return BlockRecipesBuilder.recipes[ renderingMode][ blockType ];
            } else if ( renderingMode !== DEFAULT_RENDERING_MODE && BlockRecipesBuilder.recipes[ DEFAULT_RENDERING_MODE ].hasOwnProperty( blockType ) ){
                return BlockRecipesBuilder.recipes[ DEFAULT_RENDERING_MODE ][ blockType ];
            } else {
                return BlockRecipesBuilder.getDefaultBlockRecipe( blockType );
            }
        }


        /**
         * @param {string} cacheKey Unique ID (consisting of `blockType` and
         * `renderingMode`) that will get checked for existence within the
         * local cache.
         *
         * @returns {boolean}
          */
        function isBlockCompiled( cacheKey ){
            return cachedDynamicBlocks.hasOwnProperty( cacheKey );
        }


        /**
         * Prepares compilation function along with the recipe config and
         * caches them in case the same `blockType` and `renderingMode` dynamic
         * block will be requested in the future.
         *
         * @param {string} blockType Unique identifier of the dynamic block
         * type.
         * @param {string} renderingMode Mode in which a dynamic block
         * should be rendered (eg. 'default', 'nested'...).
         *
         * @returns {{config:RecipeConfig, compile:ng.ITemplateLinkingFunction}}
         */
        function prepareDynamicBlock( blockType, renderingMode ){
            var cacheKey = blockType + '#' + renderingMode;

            if ( !isBlockCompiled( cacheKey ) ){
                var blockRecipe = getBlockDirectiveRecipe( blockType, renderingMode );

                // Compile & add to the cache
                cachedDynamicBlocks[ cacheKey ] = {
                    config:  blockRecipe.config,
                    compile: $compile( blockRecipe.template )
                };
            }

            return cachedDynamicBlocks[ cacheKey ];
        }


    }



})();
